import React, { useState, useEffect } from 'react';
import fetchHoC from 'fetch-hoc';
import compose from 'compose-function';
import { ApAuthenticationContextConsumer } from '@alixpartners/ui-utils';
import { getApiUrl, getRequestHeaders } from 'utils/api';
import { handleApiError } from 'components/ApiErrorNotification';
import { navigateToAccessDenied } from '@alixpartners/ui-components';
import { DEFAULT_RETRIES } from 'utils/api';
import { updateUrlToDemoMode } from 'utils/apiRequests';

const defaultOpts = {
	mock: false,
	name: 'response',
};

const withData = (resource, opts) => WrappedComponent =>
	class WithData extends React.Component {
		opts = { ...defaultOpts, ...opts };
		fetchComponent = null;
		Component = null;
		isUnmounting = false;

		componentWillUnmount() {
			this.isUnmounting = true;
		}

		render() {
			const { Component } = this;

			if (Component) {
				return (
					<Component
						{...this.props}
						ref={c => (this.fetchComponent = c)}
						refetch={this.handleRefetch}
					/>
				);
			}
			return (
				<ApAuthenticationContextConsumer>
					{({ fetchAuthorizationToken }) => {
						if (this.isUnmounting) return;

						fetchAuthorizationToken().then(token => {
							if (this.isUnmounting) return;

							const headers = getRequestHeaders(token);

							const { name, requestDescription, ...opts } = this.opts;
							const url = updateUrlToDemoMode(getApiUrl(resource, opts.mock));

							const mergedHeaders = { ...headers, ...opts.headers };
							const withFetch = fetchHoC(url, {
								...opts,
								headers: mergedHeaders,
							});

							const Component = compose(
								withFetch,
								withFetchPayload({ name, requestDescription, resource, url })
							)(WrappedComponent);
							this.Component = Component;
							this.forceUpdate();
						});
					}}
				</ApAuthenticationContextConsumer>
			);
		}

		handleRefetch = async () => {
			const { fetchComponent } = this;
			if (fetchComponent) {
				return fetchComponent.fetchData(fetchComponent.getUrl());
			}
		};
	};

// fetch-hoc passes it's state along as individual props so if you wanted to
// use `{...props}` on a child component it would receive all of these also.
// Instead we wrap the `fetch-hoc` props into one `response` prop so that it
// can be more easily omitted.
const withFetchPayload = ({
	name,
	requestDescription,
	resource,
	url,
}) => Component => ({
	data,
	success,
	error,
	response,
	loading,
	refetch,
	...props
}) => {
	const [retries, setRetries] = useState(DEFAULT_RETRIES);

	useEffect(
		() => {
			if (error && !loading) {
				if (
					retries > 0 &&
					error.message &&
					error.message.includes('Failed to fetch')
				) {
					setRetries(retries - 1);
					refetch();
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[error, loading]
	);

	const responseProp = {
		[name]: {
			data,
			success,
			loading,
			error,
			refetch,
			requestDescription,
			resource,
			response: response ? response.clone() : {},
		},
	};

	if (response) {
		if (response.status === 500) {
			handleApiError(data.correlationId);
		}

		if (response.status === 403) {
			navigateToAccessDenied(data.aadUrl, data.aadName);
		}
	}

	return <Component {...responseProp} {...props} />;
};

export default withData;
